import React, { ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';
import type { CSSObject } from 'styled-components';

import { mergeTokenStyles, useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

type FieldValueProps = StylableComponent<HTMLDivElement> & {
  value?: ReactNode;
  selecting?: boolean;
  disabled?: boolean;
  placeholder?: ReactNode;
};

const FieldValue = React.forwardRef<HTMLDivElement, FieldValueProps>(
  (
    { value, selecting, disabled, placeholder, style, className, ...props },
    ref,
  ) => {
    const theme = useTheme();
    const t = theme.tokens?.component?.datePicker;
    const hasValue = !!value && !disabled;

    const fieldValueStyle = useMemo(() => {
      if (!t?.fieldValue) {
        return undefined;
      }

      const parts: CSSObject[] = [t.fieldValue._ as CSSObject];

      if (selecting && t.fieldValue.selecting?.true) {
        parts.push(t.fieldValue.selecting.true as CSSObject);
      }

      parts.push(
        (hasValue
          ? t.fieldValue.hasValue?.true
          : t.fieldValue.hasValue?.false) as CSSObject,
      );

      return mergeTokenStyles(...parts);
    }, [t, selecting, hasValue]);

    return (
      <div
        ref={ref}
        className={className}
        css={[fieldValueStyle, style ?? undefined]}
        {...props}
        dir="auto"
      >
        {value || placeholder}
      </div>
    );
  },
);

FieldValue.displayName = 'FieldValue';

FieldValue.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The value to display, should be the formatted date(s) string(s) */
  value: PropTypes.any,
  /** Whether the field is selecting the date */
  selecting: PropTypes.bool,
  /** Placeholder to display when there is no value */
  placeholder: PropTypes.string,
  /** Whether the field is disabled */
  disabled: PropTypes.bool,
};

export default FieldValue;
